import React, { useState, useEffect } from 'react';
import { 
  X, Compass, Target, FileText, Search, MessageSquare,
  Check, HelpCircle, Users, ArrowRight, RefreshCw, GraduationCap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { authenticatedFetch } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Question labels for display
const QUESTION_LABELS = {
  current_situation: {
    label: 'Current Situation',
    options: {
      unemployed: 'Unemployed',
      employed_stuck: 'Employed but stuck',
      career_changer: 'Career changer',
      returning: 'Returning to work'
    }
  },
  biggest_challenge: {
    label: 'Biggest Challenge',
    options: {
      resume: 'Resume',
      finding_jobs: 'Finding the right jobs',
      interviews: 'Interviews',
      confidence: 'Confidence or clarity'
    }
  },
  knows_job_type: {
    label: 'Know What Job You Want?',
    options: {
      yes: 'Yes',
      somewhat: 'Somewhat',
      not_yet: 'Not yet'
    }
  },
  support_preference: {
    label: 'Support Preference',
    options: {
      step_by_step: 'Step-by-step guidance',
      self_paced: 'Self-paced tools'
    }
  },
  coaching_interest: {
    label: 'Coaching Interest',
    options: {
      yes: 'Yes',
      maybe: 'Maybe',
      not_now: 'Not right now'
    }
  },
  education_level: {
    label: 'Highest Education Level',
    options: {
      no_formal: 'No Formal Education',
      some_high_school: 'Some High School',
      high_school_diploma: 'High School Diploma/GED',
      some_college: 'Some College',
      associate: "Associate's Degree",
      bachelor: "Bachelor's Degree",
      master: "Master's Degree",
      doctoral: 'Doctoral Degree'
    }
  }
};

// Intent display names
const INTENT_LABELS = {
  resume_focused: { label: 'Resume-Focused', color: 'blue', icon: FileText },
  clarity_seeker: { label: 'Clarity-Seeker', color: 'purple', icon: Target },
  job_searcher: { label: 'Job Searcher', color: 'green', icon: Search },
  interview_prep: { label: 'Interview Prep', color: 'orange', icon: MessageSquare }
};

/**
 * GPS Responses Viewer Component
 * 
 * Shows the user their current GPS onboarding answers before allowing them to reset.
 * Displayed when user clicks "Personalize My Path"
 */
const GPSResponsesViewer = ({ isOpen, onClose, onReset }) => {
  const [gpsData, setGpsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadGPSData();
    }
  }, [isOpen]);

  const loadGPSData = async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/gps/status`);
      if (response.ok) {
        const data = await response.json();
        setGpsData(data);
      }
    } catch (error) {
      console.error('Failed to load GPS data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/gps/reset`, {
        method: 'POST'
      });
      if (response.ok) {
        onReset();
        onClose();
      }
    } catch (error) {
      console.error('Failed to reset GPS:', error);
    } finally {
      setResetting(false);
    }
  };

  if (!isOpen) return null;

  const responses = gpsData?.responses || {};
  const userIntent = gpsData?.user_intent;
  const intentInfo = INTENT_LABELS[userIntent] || {};
  const IntentIcon = intentInfo.icon || Target;
  const hasCompleted = gpsData?.completed;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Compass className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Your Career Path</h2>
                <p className="text-indigo-100 text-sm">Review your personalization settings</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading your responses...</p>
            </div>
          ) : !hasCompleted ? (
            <div className="text-center py-8">
              <Compass className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Personalization Yet</h3>
              <p className="text-gray-600 mb-6">
                You haven&apos;t completed the career path personalization yet.
              </p>
              <Button onClick={onReset} className="bg-indigo-600 hover:bg-indigo-700">
                Start Personalization
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          ) : (
            <>
              {/* Current Intent */}
              <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 bg-${intentInfo.color || 'indigo'}-100 rounded-lg flex items-center justify-center`}>
                    <IntentIcon className={`w-5 h-5 text-${intentInfo.color || 'indigo'}-600`} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Your Career Focus</p>
                    <h3 className="font-semibold text-gray-900">{intentInfo.label || 'General'}</h3>
                  </div>
                </div>
              </div>

              {/* Responses */}
              <h3 className="font-semibold text-gray-900 mb-4">Your Answers</h3>
              <div className="space-y-3">
                {Object.entries(QUESTION_LABELS).map(([key, config]) => {
                  const answer = responses[key];
                  const answerLabel = config.options[answer] || answer || 'Not answered';
                  
                  return (
                    <div key={key} className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">{config.label}</p>
                      <p className="font-medium text-gray-900 flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        {answerLabel}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Completed Date */}
              {gpsData?.completed_at && (
                <p className="text-sm text-gray-500 mt-4">
                  Completed on {new Date(gpsData.completed_at).toLocaleDateString()}
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {hasCompleted && (
          <div className="p-6 border-t bg-gray-50">
            <p className="text-sm text-gray-600 mb-4">
              Want to update your answers? You can retake the personalization to get new recommendations.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Keep Current
              </Button>
              <Button 
                onClick={handleReset}
                disabled={resetting}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              >
                {resetting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retake Personalization
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GPSResponsesViewer;
